import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { ORAKLE_WEATHER_WEBSITE, ORAKLE_WEATHER_LOGO_URL } from '../../constants/colors';
import { FiShield, FiMail, FiClock, FiRefreshCw, FiMaximize2 } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminAuth.css';

const ResetMFAToken = () => {
    const [email, setEmail] = useState('');
    const [localError, setLocalError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const {
        resetMfaToken,
        clearError,
        clearResetMfaTokenData,
        resetMfaTokenState,
    } = useAdminAuth();

    // Clear errors when component mounts
    useEffect(() => {
        console.log("🔧 ResetMFAToken: Component mounted, clearing data");
        clearResetMfaTokenData();
        setLocalError(null);
        setSuccessMessage(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle Redux error changes
    useEffect(() => {
        if (resetMfaTokenState.error) {
            console.log("🔧 ResetMFAToken: Redux error detected:", resetMfaTokenState.error);
            setSuccessMessage(null);
            // Ne pas effacer localError ici si c'est une erreur Redux
        }
    }, [resetMfaTokenState.error]);

    // Handle success when status 200 is received
    useEffect(() => {
        console.log("🔧 ResetMFAToken: State changed -", {
            isLoading: resetMfaTokenState.isLoading,
            hasQrImage: !!resetMfaTokenState.qrImage,
            hasSecret: !!resetMfaTokenState.secret,
            hasError: !!resetMfaTokenState.error,
            qrImageLength: resetMfaTokenState.qrImage?.length,
            secretLength: resetMfaTokenState.secret?.length
        });

        // Success: when loading finishes, has qrImage, and no error
        if (!resetMfaTokenState.isLoading &&
            resetMfaTokenState.qrImage &&
            !resetMfaTokenState.error) {
            console.log("🔧 ResetMFAToken: SUCCESS detected - QR code generated (Status 200)");
            setLocalError(null);
            const message = '✅ Token MFA réinitialisé avec succès ! Le QR code a été généré et envoyé par email.';
            setSuccessMessage(message);
            console.log("🔧 ResetMFAToken: Success message set:", message);
        }
    }, [
        resetMfaTokenState.qrImage,
        resetMfaTokenState.secret,
        resetMfaTokenState.error,
        resetMfaTokenState.isLoading
    ]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log("🔧 ResetMFAToken: Form submitted with email:", email);
        setLocalError(null);
        // Ne pas effacer successMessage ici - il sera géré par les useEffect
        clearError();

        if (!email || !email.includes('@')) {
            setLocalError('Veuillez entrer une adresse email valide');
            setSuccessMessage(null);
            return;
        }

        try {
            console.log("🔧 ResetMFAToken: Calling API /admin/mfa/reset-token with email:", email);
            await resetMfaToken(email);
            console.log("🔧 ResetMFAToken: Reset request sent successfully - waiting for response...");
            // Note: Le message de succès sera affiché automatiquement via le useEffect
            // quand la réponse avec status 200 sera reçue et traitée (isLoading passe à false)
        } catch (err) {
            console.error("🔧 ResetMFAToken: Error in handleSubmit:", err);
            const errorMessage = err instanceof Error ? err.message : 'Une erreur est survenue lors de l\'appel API /admin/mfa/reset-token';
            setLocalError(`Erreur lors de l'appel API: ${errorMessage}`);
            setSuccessMessage(null);
        }
    };

    const handleCopyQRData = () => {
        if (resetMfaTokenState.qrData) {
            navigator.clipboard.writeText(resetMfaTokenState.qrData);
            alert('Données QR copiées dans le presse-papiers');
        }
    };

    const handleClearData = () => {
        console.log("🔧 ResetMFAToken: Clearing data manually");
        clearResetMfaTokenData();
        setEmail('');
        setLocalError(null);
        setSuccessMessage(null);
    };

    return (
        <div className="admin-auth-container">
            <div className="admin-auth-card">
                <div className="admin-auth-header">
                    <div className="auth-page__logo">
                        <a href={ORAKLE_WEATHER_WEBSITE} target="_blank" rel="noopener noreferrer">
                            <img src={ORAKLE_WEATHER_LOGO_URL} alt="Orakle Weather" />
                        </a>
                    </div>
                    <div className="icon-header mb-4">
                        <FiShield size={48} className="text-white" />
                    </div>
                    <h1>Réinitialiser le token MFA</h1>
                    <p>Générez un nouveau QR code pour l'authentification MFA</p>
                </div>

                <form onSubmit={handleSubmit} className="admin-auth-form">
                    {successMessage && (
                        <div className="alert alert-success" role="alert" style={{
                            marginBottom: '20px',
                            padding: '15px',
                            backgroundColor: '#d4edda',
                            borderColor: '#c3e6cb',
                            color: '#155724'
                        }}>
                            <strong>{successMessage}</strong>
                        </div>
                    )}

                    {resetMfaTokenState.error && (
                        <div className="alert alert-danger" role="alert">
                            <strong>❌ Échec de l'API /admin/mfa/reset-token</strong><br />
                            {resetMfaTokenState.error}
                        </div>
                    )}

                    {localError && (
                        <div className="alert alert-danger" role="alert">
                            <strong>❌ Erreur lors de la requête</strong><br />
                            {localError}
                        </div>
                    )}

                    <div className="mb-3">
                        <label htmlFor="email" className="form-label">
                            <FiMail className="me-2" />
                            Adresse email
                        </label>
                        <input
                            type="email"
                            className="form-control"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="admin@example.com"
                            required
                            disabled={resetMfaTokenState.isLoading}
                        />
                    </div>

                    <button
                        type="submit"
                        className="btn btn-primary w-100 mb-3"
                        disabled={resetMfaTokenState.isLoading}
                    >
                        {resetMfaTokenState.isLoading ? 'Génération en cours...' : 'Générer le QR code'}
                    </button>

                    {resetMfaTokenState.qrImage && resetMfaTokenState.secret && (
                        <div className="qr-display mb-3">
                            <h5>QR Code MFA généré</h5>
                            <p className="text-muted">
                                Le QR code s'affiche ci-dessous ET est envoyé par email.
                            </p>
                            {resetMfaTokenState.remainingTime && (
                                <p className="text-muted">
                                    <FiClock className="me-2" />
                                    Temps restant: {resetMfaTokenState.remainingTime} secondes
                                </p>
                            )}
                            <img
                                src={resetMfaTokenState.qrImage.startsWith('data:')
                                    ? resetMfaTokenState.qrImage
                                    : `data:image/png;base64,${resetMfaTokenState.qrImage}`}
                                alt="QR Code MFA"
                                className="img-fluid"
                                style={{ maxWidth: '300px', border: '2px solid #21388B', borderRadius: '8px', padding: '10px' }}
                                onError={(e) => {
                                    console.error("🔧 ResetMFAToken: Image load error:", e);
                                    console.error("🔧 ResetMFAToken: qrImage value:", resetMfaTokenState.qrImage);
                                }}
                                onLoad={() => {
                                    console.log("🔧 ResetMFAToken: Image loaded successfully");
                                }}
                            />
                            {resetMfaTokenState.qrData && (
                                <div className="mt-3">
                                    <label className="form-label">
                                        <FiMaximize2 className="me-2" />
                                        Données QR (URL complète)
                                    </label>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={resetMfaTokenState.qrData}
                                            readOnly
                                            style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={handleCopyQRData}
                                        >
                                            Copier
                                        </button>
                                    </div>
                                </div>
                            )}
                            <button
                                type="button"
                                className="btn btn-outline-primary w-100 mt-3"
                                onClick={handleClearData}
                            >
                                <FiRefreshCw className="me-2" />
                                Générer un nouveau QR code
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default ResetMFAToken;

